import { AttendeeData, MAX_ATTENDEE_COUNT, filterAttendeeFromList } from './attendeeUtils'
import i18n from 'i18next'
import { ATTENDEE_SECTIONS } from 'common/constants/constants'

export interface CrossSectionMoveProps {
    invitedAttendees: AttendeeData[]
    presentAttendees: AttendeeData[]
    absentAttendees: AttendeeData[]
    onInvitedAttendeesChange: (attendees: AttendeeData[]) => void
    onPresentAttendeesChange: (attendees: AttendeeData[]) => void
    onAbsentAttendeesChange: (attendees: AttendeeData[]) => void
    showError: (message: string) => void
}

export class CrossSectionManager {
    private props: CrossSectionMoveProps

    constructor(props: CrossSectionMoveProps) {
        this.props = props
    }

    public updateProps(newProps: CrossSectionMoveProps) {
        this.props = newProps
    }

    public handleCrossSectionMove = (
        attendee: AttendeeData,
        targetSection: string,
        dropPosition?: number
    ): void => {
        // Reset flags when dropping into the Absent/Apologies section
        const shouldResetFlags = targetSection === ATTENDEE_SECTIONS.ABSENT

        const normalizedAttendee: AttendeeData = {
            id: attendee.id || `cross_${Date.now()}`,
            external_id: attendee.external_id,
            text: attendee.text || attendee.name || 'Unknown',
            name: attendee.name || attendee.text || 'Unknown',
            email: attendee.email || null,
            isTimeLate: shouldResetFlags ? false : attendee.isTimeLate,
            isLocationRemote: shouldResetFlags ? false : attendee.isLocationRemote
        }

        const targetDataLength = this.getTargetDataLength(targetSection)
        if (targetDataLength >= MAX_ATTENDEE_COUNT) {
            this.props.showError(i18n.t('INVALID_ATTENDEE_COUNT', { count: MAX_ATTENDEE_COUNT }))
            return
        }

        this.performCrossSectionMove(normalizedAttendee, targetSection, dropPosition)
    }

    private getTargetDataLength = (targetSection: string): number => {
        switch (targetSection) {
            case ATTENDEE_SECTIONS.INVITED:
                return this.props.invitedAttendees.length
            case ATTENDEE_SECTIONS.PRESENT:
                return this.props.presentAttendees.length
            case ATTENDEE_SECTIONS.ABSENT:
                return this.props.absentAttendees.length
            default:
                return 0
        }
    }

    private performCrossSectionMove = (
        normalizedAttendee: AttendeeData,
        targetSection: string,
        dropPosition?: number
    ): void => {
        if (targetSection === ATTENDEE_SECTIONS.INVITED) {
            this.moveToInvitedAttendees(normalizedAttendee, dropPosition)
        } else if (targetSection === ATTENDEE_SECTIONS.PRESENT) {
            this.moveToPresentAttendees(normalizedAttendee, dropPosition)
        } else if (targetSection === ATTENDEE_SECTIONS.ABSENT) {
            this.moveToAbsentAttendees(normalizedAttendee, dropPosition)
        }
    }

    private moveToInvitedAttendees = (
        normalizedAttendee: AttendeeData,
        dropPosition?: number
    ): void => {
        const updatedPresentAttendees = filterAttendeeFromList(
            this.props.presentAttendees,
            normalizedAttendee
        )
        const updatedAbsentAttendees = filterAttendeeFromList(
            this.props.absentAttendees,
            normalizedAttendee
        )

        let updatedInvitedAttendees
        if (dropPosition !== undefined && dropPosition <= this.props.invitedAttendees.length) {
            updatedInvitedAttendees = [...this.props.invitedAttendees]
            updatedInvitedAttendees.splice(dropPosition, 0, normalizedAttendee)
        } else {
            updatedInvitedAttendees = [...this.props.invitedAttendees, normalizedAttendee]
        }

        this.props.onInvitedAttendeesChange(updatedInvitedAttendees)
        this.props.onPresentAttendeesChange(updatedPresentAttendees)
        this.props.onAbsentAttendeesChange(updatedAbsentAttendees)
    }

    private moveToPresentAttendees = (
        normalizedAttendee: AttendeeData,
        dropPosition?: number
    ): void => {
        const updatedInvitedAttendees = filterAttendeeFromList(
            this.props.invitedAttendees,
            normalizedAttendee
        )
        const updatedAbsentAttendees = filterAttendeeFromList(
            this.props.absentAttendees,
            normalizedAttendee
        )

        let updatedPresentAttendees
        if (dropPosition !== undefined && dropPosition <= this.props.presentAttendees.length) {
            updatedPresentAttendees = [...this.props.presentAttendees]
            updatedPresentAttendees.splice(dropPosition, 0, normalizedAttendee)
        } else {
            updatedPresentAttendees = [...this.props.presentAttendees, normalizedAttendee]
        }

        this.props.onInvitedAttendeesChange(updatedInvitedAttendees)
        this.props.onPresentAttendeesChange(updatedPresentAttendees)
        this.props.onAbsentAttendeesChange(updatedAbsentAttendees)
    }

    private moveToAbsentAttendees = (
        normalizedAttendee: AttendeeData,
        dropPosition?: number
    ): void => {
        const updatedInvitedAttendees = filterAttendeeFromList(
            this.props.invitedAttendees,
            normalizedAttendee
        )
        const updatedPresentAttendees = filterAttendeeFromList(
            this.props.presentAttendees,
            normalizedAttendee
        )

        let updatedAbsentAttendees
        if (dropPosition !== undefined && dropPosition <= this.props.absentAttendees.length) {
            updatedAbsentAttendees = [...this.props.absentAttendees]
            updatedAbsentAttendees.splice(dropPosition, 0, normalizedAttendee)
        } else {
            updatedAbsentAttendees = [...this.props.absentAttendees, normalizedAttendee]
        }

        this.props.onInvitedAttendeesChange(updatedInvitedAttendees)
        this.props.onPresentAttendeesChange(updatedPresentAttendees)
        this.props.onAbsentAttendeesChange(updatedAbsentAttendees)
    }
}
